import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Upload, FileText, AlertCircle, CheckCircle, Clock,
  ArrowLeft, X, Database, Shield, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';

const BibleVersionImport = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // File state
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Parsed preview
  const [parsedPreview, setParsedPreview] = useState(null); // { type, totalVerses, sampleVerses, metadata }
  const [parseError, setParseError] = useState('');

  // Version metadata (editable, auto-filled from JSON)
  const [versionName, setVersionName] = useState('');
  const [shortName, setShortName] = useState('');
  const [language, setLanguage] = useState('English');
  const [description, setDescription] = useState('');

  // Import state
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null); // { current, total, message }
  const [importSuccess, setImportSuccess] = useState('');
  const [importError, setImportError] = useState('');

  // History
  const [importHistory, setImportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(true);

  // ─── Auth check ───────────────────────────────────────────────────────────
  useEffect(() => {
    checkAdmin();
  }, [user, userProfile]);

  const checkAdmin = async () => {
    if (!user && !userProfile) return; // wait for auth
    try {
      const ok = await adminService?.isAdmin();
      if (!ok) { navigate('/dashboard'); return; }
      setIsAdmin(true);
      loadHistory();
    } catch {
      navigate('/dashboard');
    } finally {
      setAuthLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const h = await adminService?.getImportHistory(20);
      setImportHistory(h || []);
    } catch (e) {
      console.error('History load error:', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ─── File handling ─────────────────────────────────────────────────────────
  const handleDrag = (e) => {
    e?.preventDefault(); e?.stopPropagation();
    setDragActive(e?.type === 'dragenter' || e?.type === 'dragover');
  };

  const handleDrop = (e) => {
    e?.preventDefault(); e?.stopPropagation();
    setDragActive(false);
    const f = e?.dataTransfer?.files?.[0];
    if (f) processFile(f);
  };

  const handleFileInput = (e) => {
    const f = e?.target?.files?.[0];
    if (f) processFile(f);
  };

  const processFile = async (file) => {
    // Reset
    setParseError('');
    setParsedPreview(null);
    setImportSuccess('');
    setImportError('');
    setVersionName('');
    setShortName('');
    setLanguage('English');
    setDescription('');

    const ext = file?.name?.split('.')?.pop()?.toLowerCase();
    const isJson = ext === 'json' || file?.type?.includes('json');
    const isCsv = ext === 'csv' || file?.type?.includes('csv') || file?.type?.includes('text');

    if (!isJson && !isCsv) {
      setParseError('Unsupported file type. Please upload a JSON or CSV file.');
      return;
    }

    if (file?.size > 20 * 1024 * 1024) {
      setParseError('File is too large. Maximum size is 20 MB.');
      return;
    }

    setSelectedFile(file);

    // Parse preview
    try {
      const content = await readFile(file);
      if (isJson) {
        await parseJsonPreview(content);
      } else {
        await parseCsvPreview(content);
      }
    } catch (err) {
      setParseError(`Could not read file: ${err?.message}`);
    }
  };

  const readFile = (file) =>
    new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = () => rej(r.error);
      r.readAsText(file);
    });

  const parseJsonPreview = async (content) => {
    try {
      const data = JSON.parse(content);
      const meta = data?.metadata || {};
      const verses = data?.verses || [];

      if (!verses?.length) {
        setParseError('No verses found in JSON file. Expected a "verses" array.');
        return;
      }

      // Auto-fill metadata
      setVersionName(meta.name || '');
      setShortName(meta.shortname || meta.module || '');
      setLanguage(meta.lang_short === 'en' ? 'English' : meta.lang || 'English');
      setDescription(meta.description ? stripHtml(meta.description)?.slice(0, 300) : '');

      setParsedPreview({
        type: 'json',
        totalVerses: verses?.length,
        sampleVerses: verses?.slice(0, 5),
        metadata: meta,
      });
    } catch (err) {
      setParseError(`Invalid JSON: ${err?.message}`);
    }
  };

  const parseCsvPreview = async (content) => {
    try {
      // The CSV format has 2 header lines before the actual headers:
      // Line 1: "Version Name"
      // Line 2: "Copyright"
      // Line 3: "Verse ID","Book Name","Book Number",Chapter,Verse,Text
      // Line 4+: data rows
      const lines = content?.split('\n')?.filter(l => l?.trim());

      // Find the actual header row (contains "Book Name" or "book_name")
      let headerLineIndex = -1;
      for (let i = 0; i < Math.min(lines?.length, 5); i++) {
        if (lines?.[i]?.toLowerCase()?.includes('book name') || lines?.[i]?.toLowerCase()?.includes('book_name')) {
          headerLineIndex = i;
          break;
        }
      }

      // Extract version name from first line if present
      if (headerLineIndex > 0) {
        const firstLine = lines?.[0]?.replace(/"/g, '')?.trim();
        if (firstLine && !firstLine?.toLowerCase()?.includes('verse') && !firstLine?.toLowerCase()?.includes('book')) {
          setVersionName(firstLine);
        }
      }

      // Count data rows (rough estimate)
      const dataRows = headerLineIndex >= 0 ? lines?.length - headerLineIndex - 1 : lines?.length - 1;

      // Parse a few sample rows
      const sampleVerses = [];
      if (headerLineIndex >= 0) {
        const headers = parseCSVLine(lines?.[headerLineIndex]);
        const bookNameIdx = headers?.findIndex(h => h?.toLowerCase()?.includes('book name') || h?.toLowerCase() === 'book_name');
        const chapterIdx = headers?.findIndex(h => h?.toLowerCase() === 'chapter');
        const verseIdx = headers?.findIndex(h => h?.toLowerCase() === 'verse');
        const textIdx = headers?.findIndex(h => h?.toLowerCase() === 'text');

        for (let i = headerLineIndex + 1; i < Math.min(headerLineIndex + 6, lines?.length); i++) {
          const cols = parseCSVLine(lines?.[i]);
          if (cols?.length > textIdx && textIdx >= 0) {
            sampleVerses?.push({
              book_name: cols?.[bookNameIdx] || '',
              chapter: cols?.[chapterIdx] || '',
              verse: cols?.[verseIdx] || '',
              text: cols?.[textIdx] || '',
            });
          }
        }
      }

      setParsedPreview({
        type: 'csv',
        totalVerses: Math.max(dataRows, 0),
        sampleVerses,
        metadata: null,
      });
    } catch (err) {
      setParseError(`Could not preview CSV: ${err?.message}`);
    }
  };

  // Simple CSV line parser (handles quoted fields)
  const parseCSVLine = (line) => {
    let result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line?.length; i++) {
      const ch = line?.[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { result?.push(current?.trim()); current = ''; }
      else { current += ch; }
    }
    result?.push(current?.trim());
    return result;
  };

  const stripHtml = (html) => html?.replace(/<[^>]*>/g, ' ')?.replace(/\s+/g, ' ')?.trim() || '';

  const clearFile = () => {
    setSelectedFile(null);
    setParsedPreview(null);
    setParseError('');
    setImportSuccess('');
    setImportError('');
    setVersionName('');
    setShortName('');
    setLanguage('English');
    setDescription('');
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  // ─── Import ────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!selectedFile) { setImportError('Please select a file first.'); return; }
    if (!versionName?.trim()) { setImportError('Please enter a version name.'); return; }

    setImporting(true);
    setImportError('');
    setImportSuccess('');
    setImportProgress({ message: 'Starting import…' });

    try {
      const ext = selectedFile?.name?.split('.')?.pop()?.toLowerCase();
      const isJson = ext === 'json' || selectedFile?.type?.includes('json');

      const versionData = {
        versionName: versionName?.trim(),
        shortVersionName: shortName?.trim() || versionName?.trim()?.split(' ')?.map(w => w?.[0])?.join('')?.toUpperCase()?.slice(0, 5),
        language: language || 'English',
        description: description?.trim() || null,
      };

      let result;

      if (isJson) {
        setImportProgress({ message: 'Parsing JSON and importing verses…' });
        result = await adminService?.importBibleVersionSimplified(selectedFile, versionData);
      } else {
        setImportProgress({ message: 'Parsing CSV and importing verses in batches…' });
        result = await adminService?.importBibleVersionBatched(selectedFile, versionData, 200);
      }

      if (result?.success) {
        const processed = result?.processed || result?.totalVerses || 0;
        const rate = result?.successRate || 100;
        setImportSuccess(`✅ Import successful! ${processed?.toLocaleString()} verses imported (${rate}% success rate) for "${versionData?.versionName}".`);
        clearFile();
        setTimeout(loadHistory, 1500);
      } else {
        throw new Error(result?.error || 'Import failed. Please check the file format and try again.');
      }
    } catch (err) {
      console.error('Import error:', err);
      setImportError(err?.message || 'An unexpected error occurred during import.');
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (d) =>
    d ? new Date(d)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  const statusIcon = (status) => {
    if (status === 'completed') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'failed') return <AlertCircle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-yellow-500" />;
  };

  const statusBadge = (status) => {
    const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
    if (status === 'completed') return `${base} bg-green-100 text-green-700`;
    if (status === 'failed') return `${base} bg-red-100 text-red-700`;
    return `${base} bg-yellow-100 text-yellow-700`;
  };

  // ─── Auth guards ───────────────────────────────────────────────────────────
  if (authLoading || (!user && !userProfile)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Checking access…</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-800">Access Denied</h2>
          <p className="text-gray-500 text-sm mt-1">Administrator privileges required.</p>
        </div>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin-dashboard" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Bible Version Import</h1>
              <p className="text-sm text-gray-500">Import Bible translations via JSON or CSV</p>
            </div>
          </div>
          <button
            onClick={loadHistory}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* ── Format Guide ── */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h2 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Supported File Formats
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-blue-800 mb-1">JSON Format</p>
              <p className="text-blue-700 mb-2">Preferred — includes metadata (name, shortname, copyright, etc.)</p>
              <pre className="bg-white border border-blue-200 rounded p-2 text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap">{`{
  "metadata": {
    "name": "American Standard Version",
    "shortname": "ASV",
    "lang_short": "en"
  },
  "verses": [
    { "book_name": "Genesis",
      "book": 1, "chapter": 1,
      "verse": 1, "text": "..." }
  ]
}`}</pre>
            </div>
            <div>
              <p className="font-medium text-blue-800 mb-1">CSV Format</p>
              <p className="text-blue-700 mb-2">Also supported — metadata auto-detected from first lines</p>
              <pre className="bg-white border border-blue-200 rounded p-2 text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap">{`"American Standard Version"
"This Bible is in the Public Domain." "Verse ID","Book Name","Book Number",Chapter,Verse,Text 1,Genesis,1,1,1,"In the beginning..." 2,Genesis,1,1,2,"And the earth was..."`}</pre>
            </div>
          </div>
        </div>

        {/* ── Upload Area ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Step 1 — Select File</h2>

          {!selectedFile ? (
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef?.current?.click()}
              className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
              }`}
            >
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">Drop your Bible file here</p>
              <p className="text-gray-400 text-sm mt-1">or click to browse — JSON or CSV, up to 20 MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.csv,.txt"
                onChange={handleFileInput}
                className="hidden"
              />
            </div>
          ) : (
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{selectedFile?.name}</p>
                  <p className="text-xs text-gray-500">{formatSize(selectedFile?.size)}</p>
                </div>
              </div>
              <button onClick={clearFile} className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          )}

          {parseError && (
            <div className="mt-3 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{parseError}</span>
            </div>
          )}
        </div>

        {/* ── Preview ── */}
        {parsedPreview && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">File Preview</h2>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${
                parsedPreview?.type === 'json' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
              }`}>
                {parsedPreview?.type}
              </span>
            </div>

            <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
              <Database className="w-4 h-4 text-gray-400" />
              <span>~{parsedPreview?.totalVerses?.toLocaleString()} verses detected</span>
            </div>

            {parsedPreview?.sampleVerses?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 border border-gray-200 text-gray-600 font-medium">Book</th>
                      <th className="text-left px-3 py-2 border border-gray-200 text-gray-600 font-medium">Ch</th>
                      <th className="text-left px-3 py-2 border border-gray-200 text-gray-600 font-medium">Vs</th>
                      <th className="text-left px-3 py-2 border border-gray-200 text-gray-600 font-medium">Text</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedPreview?.sampleVerses?.map((v, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2 border border-gray-200 text-gray-700">{v?.book_name}</td>
                        <td className="px-3 py-2 border border-gray-200 text-gray-700">{v?.chapter}</td>
                        <td className="px-3 py-2 border border-gray-200 text-gray-700">{v?.verse}</td>
                        <td className="px-3 py-2 border border-gray-200 text-gray-700 max-w-xs truncate">{v?.text}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Version Metadata ── */}
        {parsedPreview && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Step 2 — Version Details</h2>

            {importError && (
              <div className="mb-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{importError}</span>
              </div>
            )}

            {importSuccess && (
              <div className="mb-4 flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{importSuccess}</span>
              </div>
            )}

            {importing && importProgress && (
              <div className="mb-4 flex items-center gap-3 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 flex-shrink-0" />
                <span>{importProgress?.message}</span>
              </div>
            )}

            <button
              onClick={handleImport}
              disabled={importing || !versionName?.trim()}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Importing…
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Import Bible Version
                </>
              )}
            </button>

            {!versionName?.trim() && (
              <p className="text-xs text-gray-400 text-center mt-2">Enter a version name above to enable import</p>
            )}
          </div>
        )}

        {/* ── Import History ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <button
            onClick={() => setShowHistory(v => !v)}
            className="w-full flex items-center justify-between px-6 py-4 text-left"
          >
            <h2 className="text-base font-semibold text-gray-900">Import History</h2>
            {showHistory ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
          </button>

          {showHistory && (
            <div className="px-6 pb-6">
              {historyLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
                  Loading history…
                </div>
              ) : importHistory?.length === 0 ? (
                <p className="text-sm text-gray-400 py-4">No imports yet.</p>
              ) : (
                <div className="space-y-2">
                  {importHistory?.map((item, i) => (
                    <div key={item?.id || i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                      <div className="flex items-center gap-3 min-w-0">
                        {statusIcon(item?.status)}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {item?.version_name || item?.file_name || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-400">{formatDate(item?.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                        {item?.total_verses != null && (
                          <span className="text-xs text-gray-500">{Number(item?.total_verses)?.toLocaleString()} verses</span>
                        )}
                        <span className={statusBadge(item?.status)}>{item?.status || 'unknown'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default BibleVersionImport;